/**
 * Los equipos que hay de un modelo, adentro del desplegable de su fila.
 *
 * La fila de arriba es el MODELO: cuánto sale y en qué colores viene. Lo que
 * se abre es el inventario de ese modelo, que es la pregunta que sigue:
 * «¿y de cuáles tenés?».
 *
 * Cada renglón dice lo que decide la venta y nada más —el color, la batería si
 * es usado, cuántas hay y a cuánto— y se edita desde ahí, sin volver arriba.
 * El IMEI y la garantía no entran: eso se mira de a un equipo, abriendo la
 * ficha, y acá se está comparando entre varios.
 */
import { Pencil } from "lucide-react";
import { formatearNumero } from "@tora/shared";

import { Importe } from "@/components/money";
import { IconButton } from "@/components/ui/icon-button";
import { cn } from "@/lib/utils";
import { aNumero, type Producto } from "@/lib/tipos";

import { Marcas, Pendientes, type ExtrasDeFila } from "./ListaProductos";

import { MuestraColor } from "./MuestraColor";
import { nombreCompleto } from "./nombre";

export function UnidadesDelModelo({
  unidades,
  onEditar,
  vacio,
  extras,
}: {
  unidades: ReadonlyArray<Producto>;
  onEditar: (p: Producto) => void;
  /** Qué decir cuando todavía no entró ninguna. */
  vacio: string;
  /**
   * Los mismos extras que la fila usaría si estuviera arriba.
   *
   * Cuando la sección agrupa por modelo, el equipo deja de tener fila propia y
   * pasa a vivir acá. Sus chips —en taller, vendido, a pedido—, lo que le
   * falta y «ya lo tengo» tienen que venirse con él: son lo que decide si se
   * puede ofrecer, y perderlos al agrupar sería esconderlos.
   */
  extras?: ExtrasDeFila;
}): JSX.Element {
  // `vacio` en blanco: quien llama ya dijo lo que habia que decir arriba.
  if (unidades.length === 0) {
    return vacio ? <p className="text-xs text-muted-foreground">{vacio}</p> : <></>;
  }

  return (
    <ul className="divide-y divide-border">
      {unidades.map((u) => (
        <li
          key={u.id}
          className={cn(
            "flex items-center gap-3 py-1 first:pt-0 last:pb-0",
            extras?.apagada?.(u) && "opacity-60",
          )}
        >
          <MuestraColor color={u.color} />
          {u.bateria_pct !== null ? (
            <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
              bat. {u.bateria_pct}%
            </span>
          ) : null}
          <span className="flex min-w-0 flex-1 items-center gap-1.5">
            <span className="truncate text-xs text-muted-foreground">{u.codigo_interno}</span>
            <Marcas producto={u} extras={extras} />
            {extras?.chips?.(u)}
            <Pendientes producto={u} extras={extras} />
          </span>
          <span className="shrink-0 text-xs tabular-nums">
            {formatearNumero(aNumero(u.stock_actual), 0)} u.
          </span>
          <Importe
            monto={aNumero(u.precio_venta)}
            moneda={u.precio_moneda}
            tamano="sm"
            className="shrink-0"
          />
          {extras?.accion?.(u)}
          <IconButton
            icono={Pencil}
            tamano="sm"
            className="shrink-0"
            aria-label={`Editar ${nombreCompleto(u)}`}
            onClick={(e) => {
              e.stopPropagation();
              onEditar(u);
            }}
          />
        </li>
      ))}
    </ul>
  );
}
